import { useEffect, useRef } from "react";
import Hls from "hls.js";

// Plays an HLS (.m3u8) or direct video URL. Uses hls.js where needed,
// native HLS on Safari, and a plain <video> for mp4/webm.
//
// `live` = true  -> ride the live edge (used by the live preview/embed).
// `live` = false -> VOD/recording: ALWAYS start from the beginning. This is
//   critical because our recordings are written as `hls_playlist_type event`;
//   without EXT-X-ENDLIST hls.js otherwise treats them as live and jumps to the
//   end — the "started and immediately skipped to the end" bug.
export const HlsPlayer = ({ src, poster, autoPlay = false, controls = true, className = "", muted = false, live = false, mediaRef }) => {
  const ownRef = useRef(null);
  const videoRef = mediaRef || ownRef;

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return;
    const isHls = src.includes(".m3u8");
    let hls;
    let edgeTimer;

    if (isHls && Hls.isSupported()) {
      hls = new Hls({
        // lowLatencyMode off: this is standard (not LL-) HLS, and enabling it
        // makes hls.js drift/stall. We keep latency low by riding the live edge.
        lowLatencyMode: false,
        enableWorker: true,
        // Recordings/VOD must open at 0; live opens at the edge (-1).
        startPosition: live ? -1 : 0,
        // Live latency tuning: sit ~1 segment behind the edge and gently speed up
        // to catch back up if we drift, without the stalls that lowLatencyMode
        // (LL-HLS partial segments, which we don't produce) would cause.
        liveSyncDurationCount: 1,
        liveMaxLatencyDurationCount: 5,
        maxLiveSyncPlaybackRate: 1.5,
        backBufferLength: live ? 10 : 30,
        // Only report an infinite timeline for real live playback.
        liveDurationInfinity: live,
      });
      hls.loadSource(src);
      hls.attachMedia(video);
      if (autoPlay) hls.on(Hls.Events.MANIFEST_PARSED, () => video.play().catch(() => {}));

      if (live) {
        // Keep snapping to the live edge: if playback drifts more than ~4s behind
        // the newest segment (e.g. after a stall), jump forward to "now".
        const snapToLive = () => {
          try {
            const pos = hls.liveSyncPosition;
            if (pos != null && pos - video.currentTime > 3) video.currentTime = pos;
          } catch {}
        };
        hls.on(Hls.Events.LEVEL_UPDATED, snapToLive);
        edgeTimer = setInterval(snapToLive, 4000);
      }
    } else {
      // Native (Safari HLS) or direct file
      video.src = src;
      if (!live) {
        // Guard against Safari treating an EVENT playlist as live and starting
        // at the edge — force VOD/recordings to begin at the start.
        const onMeta = () => { try { if (video.currentTime > 1) video.currentTime = 0; } catch {} };
        video.addEventListener("loadedmetadata", onMeta, { once: true });
      }
      if (autoPlay) video.play().catch(() => {});
    }

    return () => { if (edgeTimer) clearInterval(edgeTimer); if (hls) hls.destroy(); };
  }, [src, autoPlay, live]);

  return (
    <video
      ref={videoRef}
      data-testid="hls-video"
      poster={poster || undefined}
      controls={controls}
      muted={muted}
      playsInline
      className={className}
      style={{ width: "100%", height: "100%", background: "#000", objectFit: "contain" }}
    />
  );
};

export default HlsPlayer;
